import { computed, inject, Injectable, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { GetFaqsResponse } from '../models/faq.models';
import { FaqService, IFaqService } from './faq-ai-intelligence-api.service';
import { LanguageService } from 'src/app/core/services/language.service';
import { ParticipantService } from 'src/app/features/session/services/participant.service';
import { ParticipantSummary } from 'src/app/features/session/models/participant-summary.model';
import { ParticipantRole } from 'src/app/features/participant-selection/models/signing-join-code.model';

export interface ParticipantSummaryView extends ParticipantSummary {
  roleDisplay: string;
}

const ROLE_DISPLAY: Record<ParticipantRole, string> = {
  [ParticipantRole.Borrower]: 'help-faq.roles.BORROWER',
  [ParticipantRole.NonBorrowingTitleHolder]: 'help-faq.roles.NBTH',
  [ParticipantRole.Seller]: 'help-faq.roles.SELLER',
  [ParticipantRole.Signer]: 'help-faq.roles.SIGNER',
  [ParticipantRole.SigningAgent]: 'help-faq.roles.SIGNING_AGENT',
  [ParticipantRole.Witness]: 'help-faq.roles.WITNESS',
  [ParticipantRole.Unknown]: '',
};

interface ClosingDisclosureFaqState {
  isLoading: boolean;
  error: string | null;
  data: GetFaqsResponse | null;
}

interface ParticipantsState {
  isLoading: boolean;
  error: string | null;
  borrowers: ParticipantSummary[];
  participants: ParticipantSummary[];
}

interface FaqState {
  closingDisclosure: ClosingDisclosureFaqState;
  participants: ParticipantsState;
}

const initialState: FaqState = {
  closingDisclosure: { isLoading: false, error: null, data: null },
  participants: { isLoading: false, error: null, borrowers: [], participants: [] },
};

@Injectable()
export class FaqAdapter {
  private readonly faqService = inject<IFaqService>(FaqService);
  private readonly languageService = inject(LanguageService);
  private readonly participantService = inject(ParticipantService);

  readonly state = signal<FaqState>(initialState);
  readonly closingDisclosureLoading = computed(() => this.state().closingDisclosure.isLoading);
  readonly closingDisclosureError = computed(() => this.state().closingDisclosure.error);
  readonly participantsLoading = computed(() => this.state().participants.isLoading);
  readonly participantsError = computed(() => this.state().participants.error);
  readonly data = computed(() => this.state().closingDisclosure.data);
  readonly hasData = computed(() => this.state().closingDisclosure.data !== null);

  readonly borrowerNames = computed((): ParticipantSummaryView[] =>
    this.state().participants.borrowers.map(p => ({ ...p, roleDisplay: ROLE_DISPLAY[p.role] })));
  readonly participantNames = computed((): ParticipantSummaryView[] =>
    this.state().participants.participants.map(p => ({ ...p, roleDisplay: ROLE_DISPLAY[p.role] })));
  readonly payoffs = computed(() => [...(this.data()?.payoffs ?? [])]);
  readonly cashToCloseFromLoanEstimate = computed(() => this.data()?.cashToCloseFromLoanEstimate ?? false);
  readonly cashToCloseFinalIsFromBorrower = computed(() => this.data()?.cashToCloseFinalIsFromBorrower ?? false);
  readonly displayFundingTransfer = computed(() => this.data()?.displayFundingTransfer ?? false);
  readonly hasNbthParticipants = computed(() => this.data()?.hasNbthParticipants ?? false);

  readonly formattedDisbursementDate = computed(() => {
    const raw = this.data()?.disbursementDate ?? null;
    if (!raw) {
      return '';
    }

    const dateObject = new Date(raw);
    const locale = this.languageService.currentLanguageDetails().locale;
    const formatter = new Intl.DateTimeFormat(locale, { month: '2-digit', day: '2-digit', year: 'numeric', timeZone: 'UTC', });
    return formatter.format(dateObject);
  });

  readonly formattedFirstPaymentDate = computed(() => {
    const raw = this.data()?.firstPaymentDate ?? null;
    if (!raw) {
      return '';
    }

    const dateObject = new Date(raw);
    const locale = this.languageService.currentLanguageDetails().locale;
    const formatter = new Intl.DateTimeFormat(locale, { month: '2-digit', day: '2-digit', year: 'numeric', timeZone: 'UTC', });
    return formatter.format(dateObject);
  });

  readonly formattedInterestRate = computed(() => {
    const raw = this.data()?.interestRate ?? null;
    if (!raw)
      return '';

    const fractionDigits = 2;
    return new Intl.NumberFormat('en-US', {
      style: 'percent',
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(raw);
  });

  readonly formattedPrepaymentPenalty = computed(() => {
    const raw = this.data()?.prepaymentPenalty ?? null;
    if (!raw)
      return '';

    return raw;
  });

  readonly formatedMonthlyPayment = computed(() => {
    const raw = this.data()?.monthlyPayment ?? null;
    if (!raw)
      return '';

    const fractionDigits = 2;
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      }).format(raw);
    
  });

  async load(signingId: string): Promise<void> {
    await Promise.allSettled([this.loadFaqs(signingId), this.loadParticipants(signingId)]);
  }

  private async loadFaqs(signingId: string): Promise<void> {
    this.state.update(s => ({ ...s, closingDisclosure: { ...s.closingDisclosure, isLoading: true, error: null } }));
    try {
      const data = await this.faqService.getFaqs(signingId);
      this.state.update(s => ({ ...s, closingDisclosure: { isLoading: false, error: null, data } }));
    } catch (err) {
      this.state.update(s => ({
        ...s,
        closingDisclosure: { ...s.closingDisclosure, isLoading: false, error: this.toMessage(err) },
      }));
    }
  }

  private async loadParticipants(signingId: string): Promise<void> {
    this.state.update(s => ({ ...s, participants: { ...s.participants, isLoading: true, error: null } }));
    try {
      const all = await firstValueFrom(this.participantService.getParticipantsForSigning(signingId));
      const borrowers = all.filter(p => p.role === ParticipantRole.Borrower);
      const others = all.filter(p => p.role !== ParticipantRole.Borrower);
      this.state.update(s => ({
        ...s,
        participants: { isLoading: false, error: null, borrowers, participants: others },
      }));
    } catch (err) {
      this.state.update(s => ({
        ...s,
        participants: { ...s.participants, isLoading: false, error: this.toMessage(err) },
      }));
    }
  }

  private toMessage(err: unknown): string {
    return err instanceof Error ? err.message : 'An unknown error occurred';
  }
}
